import { AuditPropsSchema } from './common'
import { TinyCompanySchema } from './company'
import { TinyUserSchema } from './entity'
import { EvaluationStateSchema } from './evaluation'
import { UserIdSchema } from './ids'
import { UnsavedTaskSchema } from './task/task'
import { UserProfileDataSchema } from './user'
import { z } from 'zod'

const UserSnapshotManagerSchema = z.object({
  managerId: UserIdSchema,
  employeeCount: z.number(),
  taskCount: z.number(),
})
export type UserSnapshotManager = z.infer<typeof UserSnapshotManagerSchema>

export const DecoratedUserSnapshotManagerSchema = z.object({
  ...UserSnapshotManagerSchema.shape,
  manager: TinyUserSchema,
  company: TinyCompanySchema,
})
export type DecoratedUserSnapshotManager = z.infer<typeof DecoratedUserSnapshotManagerSchema>

export const SnapshotUserSchema = z.object({
  ...UserProfileDataSchema.pick({
    firstName: true,
    lastName: true,
    lastAccess: true,
    startDate: true,
    birth: true,
    closerStatus: true,
  }).partial().shape,
})

export type SnapshotUser = z.infer<typeof SnapshotUserSchema>

const EvaluationHistorySchema = z.object({
  evalState: EvaluationStateSchema,
  eventDate: z.number().optional(),
  score: z.number().optional(),
})
export type EvaluationHistory = z.infer<typeof EvaluationHistorySchema>

/** historical data for this the current user during each time period sorted date asc */
export type HistoricalData = number[]

const TimeSeriesDataSchema = z.object({
  allTime: z.number().optional(),
  daysSinceLast: z.number().optional(),
  daily: z.array(z.number()).optional(),
  weekly: z.array(z.number()).optional(),
  monthly: z.array(z.number()).optional(),
  quarterly: z.array(z.number()).optional(),
  quarterlyAvg: z.number().optional(),
})
export type TimeSeriesData = z.infer<typeof TimeSeriesDataSchema>

const UserSnapshotSourceSchema = z.object({
  user: SnapshotUserSchema.optional(),
  createdAt: z.number().optional(),
  pitches: TimeSeriesDataSchema.optional(),
  leads: TimeSeriesDataSchema.optional(),
  sets: TimeSeriesDataSchema.optional(),
  sits: TimeSeriesDataSchema.optional(),
  closes: TimeSeriesDataSchema.optional(),
  approvals: TimeSeriesDataSchema.optional(),
  installs: TimeSeriesDataSchema.optional(),
  pitchEvaluations: EvaluationHistorySchema.array().optional(),
})
export type UserSnapshotSource = z.infer<typeof UserSnapshotSourceSchema>

const WithDaysBetweenSchema = z.object({
  /** if this can be opened more than once, the number of days to go by before opening again */
  daysBetween: z.number().optional(),
})
export type WithDaysBetween = z.infer<typeof WithDaysBetweenSchema>

const SnapshotTaskSchema = z.object({
  ...UnsavedTaskSchema.pick({
    type: true,
    priority: true,
    subject: true,
    body: true,
    required: true,
  }).shape,
  ...WithDaysBetweenSchema.shape,
})
export type SnapshotTask = z.infer<typeof SnapshotTaskSchema>

export const UserSnapshotFullSchema = z.object({
  ...AuditPropsSchema.shape,
  userId: UserIdSchema,
  managerId: UserIdSchema,
  snapshot: UserSnapshotSourceSchema.optional(),
  tasks: z.array(SnapshotTaskSchema).optional(),
})
export type UserSnapshotFull = z.infer<typeof UserSnapshotFullSchema>

export type TaskHistory = {
  type: string
  /** days since this task was completed */
  daysSince: number
}

export const sortTasksByPriorityDescending = <T extends { priority: number }>(b: T, a: T): number =>
  a.priority - b.priority
